import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import TextAlign from "@tiptap/extension-text-align";
import { Underline } from "@tiptap/extension-underline";
import Color from "@tiptap/extension-color";
import { TextStyle, FontFamily } from "@tiptap/extension-text-style";
import Image from "@tiptap/extension-image";
import Placeholder from "@tiptap/extension-placeholder";
import { useEffect, useCallback, useRef, useState } from "react";
import {
  TextB, TextItalic, TextUnderline, TextStrikethrough,
  ListBullets, ListNumbers, TextAlignLeft, TextAlignCenter, TextAlignRight,
  LinkSimple, ImageSquare, Code, Quotes, Minus, ArrowUUpLeft, ArrowUUpRight,
  BracketsCurly, BookmarkSimple, CaretDown,
} from "@phosphor-icons/react";

function ToolbarButton({ onClick, active, disabled, children, title }) {
  return (
    <button
      type="button"
      onMouseDown={e => { e.preventDefault(); onClick(); }}
      disabled={disabled}
      title={title}
      className={`p-1.5 transition-colors ${active ? "bg-[#0055FF] text-white" : "text-[#525252] hover:bg-[#F5F5F5]"} ${disabled ? "opacity-30 cursor-not-allowed" : "cursor-pointer"}`}
    >
      {children}
    </button>
  );
}

function ToolbarDivider() {
  return <div className="w-px h-5 bg-[#E5E5E5] mx-0.5" />;
}

/**
 * Toolbar dropdown — click the trigger to toggle a small menu of
 * items. Closes on outside click + Escape. Used by the Variable
 * picker and Snippet picker.
 */
function ToolbarDropdown({ trigger, items, ariaLabel, testId, emptyMessage }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={rootRef} className="relative" data-testid={testId}>
      <button
        type="button"
        aria-label={ariaLabel}
        onMouseDown={(e) => { e.preventDefault(); setOpen(v => !v); }}
        className={`inline-flex items-center gap-1 p-1.5 transition-colors ${open ? "bg-[#0055FF] text-white" : "text-[#525252] hover:bg-[#F5F5F5]"}`}
      >
        {trigger}
        <CaretDown size={9} weight="bold" />
      </button>
      {open && (
        <div
          className="absolute z-50 mt-1 left-0 min-w-[220px] max-w-[320px] max-h-[280px] overflow-y-auto bg-white border border-[#E5E5E5] shadow-lg"
          data-testid={`${testId}-menu`}
        >
          {items.length === 0 ? (
            <div className="px-3 py-2 text-[11px] text-[#A3A3A3] italic">
              {emptyMessage || "Nothing to insert"}
            </div>
          ) : items.map((it, idx) => (
            <button
              type="button"
              key={it.key || it.id || idx}
              onMouseDown={(e) => {
                e.preventDefault();
                it.onSelect?.();
                setOpen(false);
              }}
              className="w-full text-left px-3 py-2 text-xs hover:bg-[#F5F5F5] border-b border-[#F5F5F5] last:border-b-0 flex flex-col gap-0.5"
              data-testid={`${testId}-item-${it.key || it.id || idx}`}
            >
              <span className="font-bold text-[#0A0A0A]">{it.label}</span>
              {it.hint && <span className="text-[10px] text-[#A3A3A3] truncate">{it.hint}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function HtmlEditor({
  value, onChange, placeholder, minHeight = 200, testId,
  // Optional: variable picker. Pass `[{key: "{FirstName}", label: "First name"}]`
  // and a toolbar dropdown appears that inserts the `key` at the cursor.
  variables = null,
  // Optional: snippet picker. Pass `[{id, label, body_html, hint}]` and a
  // toolbar dropdown appears that inserts the `body_html` at the cursor.
  snippets = null,
}) {
  const isInternalUpdate = useRef(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      Underline,
      TextStyle,
      // Session 67 — needed so the manual-reminder default body
      // ("Verdana on dark navy") survives a parse-roundtrip in the
      // editor. Without FontFamily, TipTap drops the font-family attr
      // off the inline span and the editor preview shows the system
      // font even though the SENT email renders Verdana correctly.
      FontFamily.configure({ types: ["textStyle"] }),
      Color,
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      Link.configure({ openOnClick: false, HTMLAttributes: { class: "text-[#0055FF] underline" } }),
      Image.configure({ inline: true }),
      Placeholder.configure({ placeholder: placeholder || "Start typing..." }),
    ],
    content: value || "",
    onUpdate: ({ editor }) => {
      isInternalUpdate.current = true;
      onChange?.(editor.getHTML());
    },
    editorProps: {
      attributes: { class: "prose prose-sm max-w-none focus:outline-none min-h-[inherit] px-3 py-2" },
    },
  });

  // Sync external value changes (e.g. loading saved data)
  useEffect(() => {
    if (!editor) return;
    if (isInternalUpdate.current) {
      isInternalUpdate.current = false;
      return;
    }
    const currentHtml = editor.getHTML();
    if (value !== currentHtml && value !== undefined) {
      editor.commands.setContent(value || "", false);
    }
  }, [value, editor]);

  const addLink = useCallback(() => {
    if (!editor) return;
    const url = window.prompt("URL:", "https://");
    if (url) editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  }, [editor]);

  const addImage = useCallback(() => {
    if (!editor) return;
    const url = window.prompt("Image URL:");
    if (url) editor.chain().focus().setImage({ src: url }).run();
  }, [editor]);

  // Variable picker — drops the chosen placeholder text at the cursor
  // (and wraps it in a styled `<code>` chip so it's visually distinct
  // from regular copy in the editor).
  const insertVariable = useCallback((key) => {
    if (!editor || !key) return;
    editor.chain().focus().insertContent(
      `<code style="background:#EFF6FF;color:#0055FF;padding:1px 4px;border-radius:2px;font-size:0.9em;">${key}</code>&nbsp;`
    ).run();
  }, [editor]);

  // Snippet picker — drops a block of HTML at the cursor. Trusts the
  // backend `sanitize_rich_html` allowlist on the stored block body
  // (snippets are saved via the marketing content-blocks API).
  const insertSnippet = useCallback((html) => {
    if (!editor || !html) return;
    editor.chain().focus().insertContent(html).run();
  }, [editor]);

  if (!editor) return null;

  return (
    <div className="border border-[#E5E5E5] focus-within:border-[#0055FF] transition-colors" data-testid={testId}>
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-0.5 px-1.5 py-1 border-b border-[#E5E5E5] bg-[#FAFAFA]">
        <ToolbarButton onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive("bold")} title="Bold">
          <TextB size={15} weight="bold" />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive("italic")} title="Italic">
          <TextItalic size={15} />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive("underline")} title="Underline">
          <TextUnderline size={15} />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive("strike")} title="Strikethrough">
          <TextStrikethrough size={15} />
        </ToolbarButton>

        <ToolbarDivider />

        <ToolbarButton onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive("heading", { level: 2 })} title="Heading">
          <span className="text-xs font-black">H</span>
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive("heading", { level: 3 })} title="Subheading">
          <span className="text-[10px] font-bold">H2</span>
        </ToolbarButton>

        <ToolbarDivider />

        <ToolbarButton onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive("bulletList")} title="Bullet list">
          <ListBullets size={15} />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive("orderedList")} title="Numbered list">
          <ListNumbers size={15} />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive("blockquote")} title="Quote">
          <Quotes size={15} />
        </ToolbarButton>

        <ToolbarDivider />

        <ToolbarButton onClick={() => editor.chain().focus().setTextAlign("left").run()} active={editor.isActive({ textAlign: "left" })} title="Align left">
          <TextAlignLeft size={15} />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().setTextAlign("center").run()} active={editor.isActive({ textAlign: "center" })} title="Align center">
          <TextAlignCenter size={15} />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().setTextAlign("right").run()} active={editor.isActive({ textAlign: "right" })} title="Align right">
          <TextAlignRight size={15} />
        </ToolbarButton>

        <ToolbarDivider />

        <ToolbarButton onClick={addLink} active={editor.isActive("link")} title="Insert link">
          <LinkSimple size={15} />
        </ToolbarButton>
        <ToolbarButton onClick={addImage} title="Insert image">
          <ImageSquare size={15} />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleCodeBlock().run()} active={editor.isActive("codeBlock")} title="Code block">
          <Code size={15} />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().setHorizontalRule().run()} title="Horizontal rule">
          <Minus size={15} />
        </ToolbarButton>

        <ToolbarDivider />

        <ToolbarButton onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} title="Undo">
          <ArrowUUpLeft size={15} />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} title="Redo">
          <ArrowUUpRight size={15} />
        </ToolbarButton>

        {(variables?.length || snippets?.length) ? <ToolbarDivider /> : null}

        {variables?.length ? (
          <ToolbarDropdown
            ariaLabel="Insert variable"
            testId={`${testId || "html-editor"}-variable-picker`}
            emptyMessage="No variables available"
            trigger={
              <span className="flex items-center gap-1">
                <BracketsCurly size={14} weight="bold" />
                <span className="text-[10px] font-bold uppercase tracking-wider">Variable</span>
              </span>
            }
            items={variables.map(v => ({
              key: v.key,
              label: v.label,
              hint: v.key,
              onSelect: () => insertVariable(v.key),
            }))}
          />
        ) : null}

        {snippets?.length ? (
          <ToolbarDropdown
            ariaLabel="Insert snippet"
            testId={`${testId || "html-editor"}-snippet-picker`}
            emptyMessage="No snippets available"
            trigger={
              <span className="flex items-center gap-1">
                <BookmarkSimple size={14} weight="bold" />
                <span className="text-[10px] font-bold uppercase tracking-wider">Snippet</span>
              </span>
            }
            items={snippets.map(s => ({
              id: s.id,
              label: s.label,
              hint: s.hint,
              onSelect: () => insertSnippet(s.body_html),
            }))}
          />
        ) : null}
      </div>

      {/* Editor area */}
      <div style={{ minHeight }} className="bg-white">
        <EditorContent editor={editor} />
      </div>

      {/* Variable hint — legacy hash-style hint. Hidden when the
          caller supplies a structured `variables` prop (which surfaces
          the same info via a dropdown picker instead). */}
      {!variables?.length && (
        <div className="px-3 py-1.5 border-t border-[#F5F5F5] bg-[#FAFAFA]">
          <p className="text-[10px] text-[#A3A3A3]">
            Variables: <code className="bg-[#F5F5F5] px-1 text-[#525252]">#companyname#</code> <code className="bg-[#F5F5F5] px-1 text-[#525252]">#fullname#</code> <code className="bg-[#F5F5F5] px-1 text-[#525252]">#firstname#</code> <code className="bg-[#F5F5F5] px-1 text-[#525252]">#lastname#</code> <code className="bg-[#F5F5F5] px-1 text-[#525252]">#contactemail#</code>
          </p>
        </div>
      )}
    </div>
  );
}
